import { useEffect, useRef } from "react";
import { CH2_MORNING, QUAKE, KNOCK, WOLVES_END, WAITING, CH2_END } from "./chapter2";
import { CH3_OPEN, CHAMBER, CULT, JOE3, AGREE, RITUAL, FEEL, FINAL } from "./chapter3";
import { CHAPTER_END, PLAN, DAY4, CAVE_WARM, CAVE_COLD, ROOMS, HOUSEMATE_IDS, LOG_IDS, DINNER, FIRESIDE, ALONE, HANGOVER, PANELS3_ID, SCENES, STATION_ID, STATION2_ID, PANELS_ID, PANELS2_ID, DAY2_ID, visible } from "./rooms";
import { W, H } from "./props";
import { renderScene, doorWidth } from "./draw";
import { updateAudio, stepSound, doorSound, lockedSound, pickupSound, cutsceneAudio, sting, beepSound } from "./audio";
import { CUTSCENES } from "./cutscene";

const SPEED = 62;
const REACH = 16;

function targets(room, inv, rid, notes) {
  const list = [];
  room.doors.forEach((d) => visible(d, notes) && list.push({ kind: "door", cx: d.x + doorWidth(d) / 2, d,
    label: d.prompt || (d.locked ? `Examine ${d.label}` : d.kind === "stairs_up" || d.kind === "stairs_down" ? `Take stairs: ${d.label}` : `Enter ${d.label}`) }));
  (room.npcs || []).forEach((n) => visible(n, notes) && list.push({ kind: "npc", cx: n.x + (n.cx ?? 4), n, label: `${n.verb || "Talk to"} ${n.name}`, note: `${rid}:npc:${n.x}` }));
  room.items.forEach((it) => !inv.includes(it.id) && visible(it, notes) && list.push({ kind: "item", cx: it.x + 2, it, label: `Pick up ${it.name}` }));
  room.props.forEach((p) => p.desc && visible(p, notes) && list.push({ kind: "prop", cx: p.x + 10, text: p.desc, label: "Examine", note: `${rid}:${p.type}:${p.x}` }));
  room.windows.forEach((w) => list.push({ kind: "prop", cx: w.x + 17, label: "Look outside",
    text: "The snowstorm howls against the glass. You can't see more than a few feet." }));
  return list;
}

function spawnX(toRoom, fromId) {
  const d = ROOMS[toRoom].doors.find((dd) => dd.to === fromId);
  return d ? d.x + doorWidth(d) / 2 - 4 : 40;
}

export const GameCanvas = ({ initial, input, pausedRef, menuRef, engineRef, onPrompt, onDialog, onCloseDialog, onPickup, onRoomChange, onNote, onCutscene, onAllFound }) => {
  const canvasRef = useRef(null);
  const cb = useRef({});
  cb.current = { onPrompt, onDialog, onCloseDialog, onPickup, onRoomChange, onNote, onCutscene, onAllFound };

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    const st = { room: ROOMS[initial.room] ? initial.room : "hallway", x: initial.x, facing: 1, moving: false, walk: 0,
      cam: 0, fade: 1, fadeDir: -1, pending: null, inventory: [...initial.inventory], notes: [...(initial.notes || [])], prompt: null, edgeShown: false };
    engineRef.current = st;
    let last = performance.now(), raf;

    const addNote = (id) => { if (!st.notes.includes(id)) { st.notes.push(id); cb.current.onNote(id); } };
    const interact = () => {
      if (menuRef.current) return;
      if (pausedRef.current) return cb.current.onCloseDialog();
      if (st.pending) return;
      const room = ROOMS[st.room];
      const tg = targets(room, st.inventory, st.room, st.notes).map((g) => ({ ...g, dist: Math.abs(g.cx - (st.x + 4)) }))
        .filter((g) => g.dist < REACH).sort((a, b) => a.dist - b.dist)[0];
      if (!tg) return;
      if (tg.kind === "door") {
        if (tg.d.locked) { lockedSound(); addNote(`${st.room}:door:${tg.d.x}`); return cb.current.onDialog(tg.d.desc); }
        if (tg.d.requires && ![].concat(tg.d.requires).some((id) => st.notes.includes(id))) return cb.current.onDialog(tg.d.lockedText);
        doorSound(tg.d.kind); st.pending = tg.d.to; st.pendingCut = tg.d.cutscene; st.pendingCutEvent = tg.d.event; st.fadeDir = 1;
      } else if (tg.kind === "item") {
        st.inventory.push(tg.it.id); pickupSound();
        cb.current.onPickup(tg.it);
      } else if (tg.kind === "npc") {
        addNote(tg.note);
        if (tg.n.then) st.afterDialog = tg.n.then;
        cb.current.onDialog(tg.n.lines);
      }
      else { if (tg.note) addNote(tg.note); cb.current.onDialog(tg.text); }
    };

    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000); last = now;
      if (st.cutscene) {
        const cs = st.cutscene, sc = CUTSCENES[cs.list[cs.idx]], tt = (now - cs.start) / 1000;
        if (cs.held) {
          if (input.current.tap) { input.current.tap = false; if (pausedRef.current && !menuRef.current) cb.current.onCloseDialog(); }
          sc.draw(ctx, sc.duration - 1.05, now); raf = requestAnimationFrame(loop); return;
        }
        if (input.current.tap) { input.current.tap = false; if (!menuRef.current && tt > 1) cs.skip = true; }
        const cap = sc.caption(tt);
        if (cap !== cs.caption) { cs.caption = cap; cb.current.onCutscene(true, cap); }
        if (sc.sting && !cs.stung && tt > sc.sting) { cs.stung = true; sting(); }
        if (tt < sc.duration && !cs.skip) { sc.draw(ctx, tt, now); raf = requestAnimationFrame(loop); return; }
        if (sc.hold) {
          sc.draw(ctx, sc.duration - 1.05, now);
          if (!cs.held) { cs.held = true; cb.current.onCutscene(false, null); const ev = cs.event ? cs.event(st.notes) : st.pendingEvent; st.pendingEvent = null; if (ev && !st.notes.includes(ev.id)) { addNote(ev.id); cb.current.onDialog(ev.lines); } }
          raf = requestAnimationFrame(loop); return;
        }
        if (cs.idx < cs.list.length - 1) {
          cs.idx += 1; cs.start = now; cs.skip = false; cs.stung = false; cutsceneAudio(cs.list[cs.idx]);
          raf = requestAnimationFrame(loop); return;
        }
        cutsceneAudio(null); st.cutscene = null; st.x = spawnX(cs.to, cs.from); st.room = cs.to;
        if (cs.event) { const ev = cs.event(st.notes); if (!st.notes.includes(ev.id)) st.pendingEvent = ev; }
        st.fade = 1; st.fadeDir = -1; st.ignoreTapUntil = now + 1200; cb.current.onCutscene(false, null); cb.current.onRoomChange(st.room, st.x);
        const welcome = ROOMS[st.room].items.find((it) => it.welcome && !st.inventory.includes(it.id));
        if (welcome) setTimeout(() => { if (st.room !== cs.to || st.inventory.includes(welcome.id)) return; st.inventory.push(welcome.id); pickupSound(); cb.current.onPickup(welcome); }, 900);
      }
      const has = (id) => st.notes.includes(id);
      if (st.scene && st.scene.phase === "talking" && !pausedRef.current) {
        const end = SCENES[st.scene.key].end; st.scene = null;
        st.room = end.room; st.x = end.x; st.fade = 1; st.fadeDir = -1; st.pendingEvent = end.event || null;
        cb.current.onRoomChange(st.room, st.x);
      }
      if (st.afterDialog && !pausedRef.current && !st.pending && !st.fadeDir) {
        st.pending = st.afterDialog.room; st.pendingX = st.afterDialog.x; st.afterDialog = null; st.fadeDir = 1;
      }
      const day1 = has(DINNER.id) && !has(FIRESIDE.id), day2 = has(DAY2_ID) && !has(ALONE.id), day3 = has(HANGOVER.id) && !has(PLAN.id), day4 = has(DAY4.id) && !has(CHAPTER_END.id), ch2d = has(CH2_MORNING.id) && !has(QUAKE.id);
      st.morning = (day1 || day2 || day3 || day4 || ch2d) && !st.scene;
      st.lightsOff = (day1 && !has(PANELS_ID)) || (day2 && !has(PANELS2_ID)) || (day3 && !has(PANELS3_ID)) || (day4 && !has("outside:npc:479")) || (ch2d && !has("outside:npc:480"));
      const free = !st.scene && !st.pending && !st.fadeDir && !pausedRef.current && !menuRef.current && !st.afterDialog;
      const startScene = (key) => { st.pending = SCENES[key].room; st.pendingScene = key; st.fadeDir = 1; };
      if (free && !has(DINNER.id) && HOUSEMATE_IDS.every(has) && LOG_IDS.every((id) => st.inventory.includes(id))) { startScene("dinner"); cb.current.onAllFound(); }
      else if (free && has(STATION_ID) && !has(FIRESIDE.id)) startScene("fireside");
      else if (free && has(STATION2_ID) && !has(ALONE.id)) startScene("alone");
      else if (free && has(ALONE.id) && !has(HANGOVER.id)) startScene("hangover");
      else if (free && (has(CAVE_WARM.id) || has(CAVE_COLD.id)) && !has(PLAN.id)) startScene("plan");
      else if (free && has("ch2:start") && !has("living:event:council")) startScene(has("hallway:choice:open") ? "council_warm" : "council_cold");
      else if (free && has("bedroom:npc:91") && !has(CH2_MORNING.id)) { st.pending = "hallway"; st.pendingX = 40; st.pendingCutEvent = () => CH2_MORNING; st.fadeDir = 1; }
      else if (free && has("trail_north:npc:420") && !has(QUAKE.id)) { st.pending = "trail_north"; st.pendingCut = ["quake"]; st.pendingCutEvent = () => QUAKE; st.fadeDir = 1; }
      else if (free && st.room === "outside" && has(QUAKE.id) && !has(WAITING.id)) startScene("waiting");
      else if (free && has(KNOCK.id) && !has(WOLVES_END.id)) { st.pending = "living"; st.pendingCut = ["wolves"]; st.pendingCutEvent = () => WOLVES_END; st.fadeDir = 1; }
      else if (free && has("ch3:start") && !has(CH3_OPEN.id)) { st.pending = "iron_stairs"; st.pendingX = st.x; st.pendingCutEvent = () => CH3_OPEN; st.fadeDir = 1; }
      else if (free && has(AGREE.id) && !has(RITUAL.id)) { st.pending = "chamber"; st.pendingX = st.x; st.pendingCutEvent = () => RITUAL; st.fadeDir = 1; }
      else if (free && has(RITUAL.id) && !has(FEEL.id)) { st.pending = "living"; st.pendingX = 150; st.pendingCut = ["assault"]; st.pendingCutEvent = () => FEEL; st.fadeDir = 1; }
      else if (free && st.room === "outside" && has(FEEL.id) && !has(FINAL.id)) { st.pending = "outside"; st.pendingX = st.x; st.pendingCut = ["godrise"]; st.pendingCutEvent = () => FINAL; st.fadeDir = 1; }
      else if (free && has(CULT.id) && !has(JOE3.id)) { st.pending = "living"; st.pendingX = 150; st.pendingCut = ["town"]; st.pendingCutEvent = () => JOE3; st.fadeDir = 1; }
      else if (free && has("living:npc:286") && !has(AGREE.id)) { st.pending = "chamber"; st.pendingX = 250; st.pendingCut = ["mine"]; st.pendingCutEvent = () => AGREE; st.fadeDir = 1; }
      else if (free && has(CHAMBER.id) && !has(CULT.id)) { st.pending = "chamber"; st.pendingX = st.x; st.pendingCutEvent = () => CULT; st.fadeDir = 1; }
      else if (free && has("basement_hall:npc:400") && !has(CH2_END.id)) { st.pending = "living"; st.pendingX = 150; st.pendingCut = ["descent"]; st.pendingCutEvent = () => CH2_END; st.fadeDir = 1; }
      else if (free && has("cave_tunnel:npc:548") && !has(CHAPTER_END.id)) { st.pending = "outside"; st.pendingCut = ["homeward"]; st.pendingCutEvent = () => CHAPTER_END; st.fadeDir = 1; }
      if (st.bg === "memory" && now - (st.lastBeep || 0) > 1100) { st.lastBeep = now; beepSound(); }
      const room = ROOMS[st.room];
      if (input.current.tap) { input.current.tap = false; if (!(now < (st.ignoreTapUntil || 0))) interact(); }
      const dir = pausedRef.current || menuRef.current || st.pending || st.scene ? 0 : (input.current.right ? 1 : 0) - (input.current.left ? 1 : 0);
      st.moving = dir !== 0;
      if (dir) {
        st.facing = dir; st.walk += 1;
        const nx = Math.max(4, Math.min(room.width - 12, st.x + dir * SPEED * dt));
        st.stepDist = (st.stepDist || 0) + Math.abs(nx - st.x); st.x = nx;
        if (st.stepDist > 11) { st.stepDist = 0; stepSound(room); }
        if (room.edgeText && st.x >= room.width - 13 && !st.edgeShown) { st.edgeShown = true; cb.current.onDialog(room.edgeText); }
        if (st.x < room.width - 30) st.edgeShown = false;
      }
      if (st.fadeDir) {
        st.fade = Math.max(0, Math.min(1, st.fade + st.fadeDir * dt * 3.5));
        if (st.fade >= 1 && st.pending) {
          const from = st.room, to = st.pending; st.pending = null;
          if (st.pendingScene) {
            const sc = SCENES[st.pendingScene]; st.scene = { key: st.pendingScene, phase: "seated" }; st.pendingScene = null;
            st.room = sc.room; st.x = sc.x; st.fadeDir = -1;
            cb.current.onRoomChange(st.room, st.x);
          } else if (st.pendingCut) {
            const list = [].concat(st.pendingCut); st.pendingCut = null; st.fadeDir = 0;
            st.cutscene = { to, from, start: now, list, idx: 0, event: st.pendingCutEvent }; st.pendingCutEvent = null; st.prompt = null; cb.current.onPrompt(null); cutsceneAudio(list[0]); cb.current.onCutscene(true, null);
          } else {
            if (st.pendingCutEvent) { const ev = st.pendingCutEvent(st.notes); if (!st.notes.includes(ev.id)) st.pendingEvent = ev; st.pendingCutEvent = null; }
            st.room = to; st.x = st.pendingX ?? spawnX(to, from); st.pendingX = null; st.fadeDir = -1;
            cb.current.onRoomChange(st.room, st.x);
          }
        } else if (st.fade <= 0) {
          st.fadeDir = 0;
          if (st.pendingEvent) { const ev = st.pendingEvent; st.pendingEvent = null; addNote(ev.id); cb.current.onDialog(ev.lines); }
          if (st.scene && st.scene.phase === "seated") { const ev = SCENES[st.scene.key].event; st.scene.phase = "talking"; addNote(ev.id); cb.current.onDialog(ev.lines); }
        }
      }
      const cur = ROOMS[st.room];
      st.cam = cur.width <= W ? -(W - cur.width) / 2 : Math.max(0, Math.min(cur.width - W, st.x - W / 2));
      const near = st.pending || st.cutscene ? null : targets(cur, st.inventory, st.room, st.notes).map((g) => ({ ...g, dist: Math.abs(g.cx - (st.x + 4)) }))
        .filter((g) => g.dist < REACH).sort((a, b) => a.dist - b.dist)[0];
      const label = near && !pausedRef.current && !menuRef.current && !st.scene ? near.label : null;
      if (label !== st.prompt) { st.prompt = label; cb.current.onPrompt(label); }
      updateAudio(st, cur);
      renderScene(ctx, st, cur, now);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <canvas ref={canvasRef} width={W} height={H} data-testid="game-canvas"
      onPointerDown={() => { if (engineRef.current?.cutscene) input.current.tap = true; }}
      className="w-full h-auto block bg-black" style={{ imageRendering: "pixelated" }} />
  );
};
